import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Save, Loader2, AlertTriangle, X } from "lucide-react";
import Seo, { breadcrumbs } from "../components/Seo";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";

import {
  computeOhioTax, computeMonthlyPayment,
  FEE_DOC, FEE_STATE, FEE_TITLE,
  CREDIT_TIERS, OHIO_COUNTIES,
} from "../data/finance";

import ByodProgressBar from "../components/byod/ByodProgressBar";
import { useByodState, STEPS } from "../components/byod/useByodState";
import StartStep from "../components/byod/StartStep";
import VehicleStep from "../components/byod/VehicleStep";
import TradeStep from "../components/byod/TradeStep";
import MoneyDownStep from "../components/byod/MoneyDownStep";
import RebatesStep from "../components/byod/RebatesStep";
import RateStep from "../components/byod/RateStep";
import AccessoriesStep from "../components/byod/AccessoriesStep";
import RequiredProtectionStep from "../components/byod/RequiredProtectionStep";
import OptionalProtectionStep from "../components/byod/OptionalProtectionStep";
import SalesTaxStep from "../components/byod/SalesTaxStep";
import ReviewStep from "../components/byod/ReviewStep";
import SubmitStep from "../components/byod/SubmitStep";

/**
 * Build My Own Deal — guided wizard experience.
 *
 * One step at a time, with slide-in transitions. State persists in
 * localStorage (and the server, for signed-in customers).
 *
 * Strict guardrails per project owner:
 *   • Pricing, Ohio tax, doc/state/title fees, rebate stacking, APR
 *     overrides, and vAuto pricing are NOT changed here.
 *   • Vehicle prefill routing (the ?vin=, ?stock=, ?title= URL params)
 *     is identical to the previous BuildYourOwnDeal page.
 */
export default function BuildMyOwnDeal() {
  const [sp] = useSearchParams();
  const vinParam = (sp.get("vin") || "").trim().toUpperCase();
  const stockParam = (sp.get("stock") || "").trim();
  const titleParam = sp.get("title") || "";
  const statusParam = sp.get("status") || "";
  const urlParam = sp.get("url") || "";

  // Live vehicle resolution (identical pattern to BuildYourOwnDeal v6.5.x)
  const [vehicle, setVehicle] = useState(null);
  const [vehicleStatus, setVehicleStatus] = useState(vinParam || stockParam ? "loading" : "none");

  useEffect(() => {
    if (!vinParam && !stockParam) return undefined;
    let on = true;
    (async () => {
      try {
        const q = vinParam || stockParam;
        const { data } = await api.get("/vehicles", { params: { search: q, limit: 5 } });
        if (!on) return;
        const items = data?.items || [];
        const hit = items.find((it) =>
          (vinParam && (it.vin || "").toUpperCase() === vinParam) ||
          (stockParam && String(it.stock_number || "") === stockParam)
        ) || items[0];
        if (hit) {
          setVehicle(hit);
          setVehicleStatus("found");
        } else {
          setVehicleStatus("not_found");
        }
      } catch {
        if (on) setVehicleStatus("not_found");
      }
    })();
    return () => { on = false; };
  }, [vinParam, stockParam]);

  // Wizard state
  const byod = useByodState();
  const { user } = useAuth();
  const isLoggedIn = !!(user && typeof user === "object");
  const navigate = useNavigate();
  const location = useLocation();
  const dealTopRef = useRef(null);

  // ---- Scroll to top of the Deal Desk content on every step change ----
  // Lands the user just below the site header (which is sticky), never at
  // the bottom of the previous step. Smooth on both desktop and mobile.
  useEffect(() => {
    if (!dealTopRef.current) return;
    // Defer one frame so the new step is in the DOM before we scroll.
    const t = setTimeout(() => {
      try {
        dealTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 20);
    return () => clearTimeout(t);
  }, [byod.stepIdx]);

  // ---- Modal state for guarded actions ----
  const [vehicleChangeModal, setVehicleChangeModal] = useState(null);
  // shape: { newVin, newStock, prevVin } — only set when a vehicle change is intercepted
  const [leaveModal, setLeaveModal] = useState(null);
  // shape: { target } — target is a string URL (anchor click) or -1 (browser back)
  const [showSaveGate, setShowSaveGate] = useState(false);

  // Compute deal math centrally so Review and Submit share one source.
  const dealMath = useMemo(() => {
    const s = byod.state;
    const price = Number(vehicle?.price || 0);
    const tradeValue = s.trade?.has_trade === "yes" ? byod.tradeSelectedValue : 0;
    const tradePayoff = s.trade?.has_trade === "yes" ? (Number(s.trade?.payoff || 0) || 0) : 0;
    const netTrade = tradeValue - tradePayoff;
    const rebatesTotal = Number(s.rebates?.total_savings_estimated || 0);
    const accessoriesTotal = byod.accessoriesTotal;
    const coveragesTotal = byod.coveragesTotal;
    const down = byod.moneyDownAmount;
    const countyName = s.sales_tax?.county || "Franklin";
    const countyRow = OHIO_COUNTIES.find((c) => c.name === countyName) || OHIO_COUNTIES[0];
    const tax = price > 0 ? computeOhioTax({ price, tradeValue, rebates: rebatesTotal, rate: countyRow.rate }) : 0;
    const fees = FEE_DOC + FEE_STATE + FEE_TITLE;
    const taxable = Math.max(0, price - tradeValue);
    const amountFinanced = price > 0
      ? Math.max(0, price + tax + fees + accessoriesTotal + coveragesTotal - down - netTrade - rebatesTotal)
      : 0;
    // Rate selection — customer-picked tier from Step 6, or fall back to "good".
    const tierValue = s.rate?.credit_tier || "good";
    const tier = CREDIT_TIERS.find((t) => t.value === tierValue) || CREDIT_TIERS[1];
    const isNew = (vehicle?.status || "").toLowerCase() === "new";
    const baseRate = isNew ? tier.new : tier.used;
    const aprOverride = s.rate?.special_apr_pct;
    const aprTerm = s.rate?.special_apr_term;
    const termSelected = s.rate?.term || 72;
    let monthly = 0;
    if (amountFinanced > 0) {
      if (aprOverride != null && aprTerm) {
        monthly = computeMonthlyPayment({ amountFinanced, annualRate: aprOverride / 100, months: aprTerm });
      } else {
        monthly = computeMonthlyPayment({ amountFinanced, annualRate: baseRate, months: termSelected });
      }
    }
    return {
      tax, taxable, fees, amountFinanced,
      countyName, countyRate: countyRow.rate,
      estimatedMonthly: monthly,
      // Back-compat keys for SubmitStep buildLeadPayload — both equal the
      // single monthly estimate now that we no longer show a range.
      estimatedLow: monthly,
      estimatedHigh: monthly,
    };
  }, [byod.state, vehicle, byod.tradeSelectedValue, byod.moneyDownAmount, byod.accessoriesTotal, byod.coveragesTotal]);

  // ----- Server-side draft (account users only) -----
  const persistToServer = useCallback(async () => {
    if (!isLoggedIn) return false;
    try {
      await api.post("/byod/save", {
        state: byod.state,
        step_key: STEPS[byod.stepIdx]?.key || null,
        vehicle_title: vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}`.trim() : null,
        vin: vehicle?.vin || null,
        stock_number: vehicle?.stock_number || null,
      });
      byod.markSaved();
      return true;
    } catch {
      return false;
    }
  }, [isLoggedIn, byod, vehicle]);

  // Auto-persist when account user advances steps
  useEffect(() => {
    if (isLoggedIn) persistToServer();
  }, [isLoggedIn, byod.stepIdx, persistToServer]);

  // Hydrate from server when an account user lands on the page WITHOUT existing local draft
  useEffect(() => {
    if (!isLoggedIn) return;
    let on = true;
    (async () => {
      try {
        const { data } = await api.get("/byod/load", { params: vinParam ? { vin: vinParam } : {} });
        if (!on || !data?.draft?.state) return;
        // Only hydrate if our local state is the default (empty start)
        if (!byod.state.identity?.mode && (byod.state.trade?.has_trade == null)) {
          const draft = data.draft.state;
          // Mark as a saved deal we intentionally opened so vehicle-bind guards
          // don't fire and so isDirty stays false until the customer changes
          // something post-load.
          const hydrated = {
            ...draft,
            meta: {
              ...(draft.meta || {}),
              bound_vin: data.draft.vin || vinParam || draft?.meta?.bound_vin || null,
              opened_saved_deal: true,
              last_saved_at: data.draft.updated_at || new Date().toISOString(),
            },
          };
          byod.setState(hydrated);
          // Stamp signature so isDirty is false immediately after hydration.
          setTimeout(() => byod.markSaved(), 0);
        }
      } catch { /* ignore */ }
    })();
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, vinParam]);

  // ---- Vehicle change detection ----
  // Bind on first vehicle resolution. If the URL VIN differs from the
  // bound VIN later, either reset (if the wizard is untouched) or open
  // the "save before changing vehicles?" modal (if it's dirty).
  const resetForNewVehicle = useCallback((newVin) => {
    // Wipe everything except the customer's identity (name/email don't
    // belong to the prior vehicle). All deal-specific fields revert to
    // defaults, and the wizard returns to Step 1 (Vehicle, since Start
    // is auto-complete once identity exists).
    const keepIdentity = byod.state.identity || {};
    const keepContact = byod.state.contact || {};
    byod.setState({
      ...JSON.parse(JSON.stringify({})), // safety wipe
      identity: keepIdentity,
      contact: keepContact,
      trade: { has_trade: null, year: "", make: "", model: "", mileage: "", payoff: "", appraisal_low: "", appraisal_high: "", selected_value: null },
      money_down: { choice: null, custom_amount: "" },
      rebates: { selected: [], not_sure: [], skipped: [], total_savings_estimated: 0, acknowledged: false },
      rate: { credit_tier: "good", term: 72, special_apr_id: null, special_apr_pct: null, special_apr_term: null, special_apr_name: null },
      accessories: [],
      accessory_site_interest: false,
      required_protection: { states: {}, expanded: {} },
      optional_protection: { states: {}, expanded: {} },
      sales_tax: { county: "Franklin" },
      review: {},
      meta: {
        bound_vin: newVin || null,
        last_saved_at: null,
        last_saved_signature: null,
        opened_saved_deal: false,
      },
    });
    byod.goTo(keepIdentity?.mode ? 1 : 0);
    // Re-sign the snapshot so isDirty starts false.
    setTimeout(() => byod.markSaved(), 0);
  }, [byod]);

  useEffect(() => {
    if (!vinParam) return;
    const boundVin = byod.state.meta?.bound_vin || null;
    if (!boundVin) {
      // First time we see a vehicle in this wizard session — bind it.
      byod.update("meta", { bound_vin: vinParam });
      setTimeout(() => byod.markSaved(), 0);
      return;
    }
    if (boundVin.toUpperCase() === vinParam) return; // same vehicle
    // Different vehicle than what we bound to.
    if (byod.isDirty) {
      setVehicleChangeModal({ newVin: vinParam, prevVin: boundVin });
    } else {
      // Clean wizard — silently reset and bind to the new VIN.
      resetForNewVehicle(vinParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vinParam, byod.state.meta?.bound_vin]);

  // ---- Leave-page guard ----
  // 1) beforeunload — covers tab close / refresh / typed URL / external link.
  // 2) Click capture — covers React Router <Link> clicks to a different
  //    pathname while still on the BMOD page.
  // 3) popstate — soft-blocks the browser back button.
  useEffect(() => {
    if (!byod.isDirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    const onClick = (e) => {
      // Allow clicks inside the leave/vehicle modals themselves.
      if (e.target.closest && e.target.closest("[data-byod-modal]")) return;
      const link = e.target.closest && e.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      if (link.target === "_blank") return;
      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch { return; }
      // External link → let beforeunload do its job.
      if (url.origin !== window.location.origin) return;
      // Same pathname (search/hash navigation) → allow.
      if (url.pathname === window.location.pathname) return;
      // Intercept and open the leave modal.
      e.preventDefault();
      e.stopPropagation();
      setLeaveModal({ target: url.pathname + url.search + url.hash });
    };
    document.addEventListener("click", onClick, true);

    // Browser back-button guard: push a sentinel so the next back triggers
    // our handler; if the user cancels we re-arm the sentinel.
    if (!window.history.state || !window.history.state.byodGuard) {
      window.history.pushState({ ...(window.history.state || {}), byodGuard: true }, "");
    }
    const onPop = () => {
      // The browser already moved back; we re-push and show the modal so
      // the customer can choose to leave properly.
      window.history.pushState({ byodGuard: true }, "");
      setLeaveModal({ target: -1 });
    };
    window.addEventListener("popstate", onPop);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onPop);
    };
  }, [byod.isDirty]);

  // Modal action helpers
  const handleVehicleChange = async (action) => {
    const m = vehicleChangeModal;
    if (!m) return;
    if (action === "keep") {
      // Revert URL back to the bound VIN so we don't keep re-triggering.
      const sp2 = new URLSearchParams(location.search);
      sp2.set("vin", m.prevVin);
      navigate(`${location.pathname}?${sp2.toString()}`, { replace: true });
      setVehicleChangeModal(null);
      return;
    }
    if (action === "save") {
      const ok = await persistToServer();
      if (!ok && !isLoggedIn) {
        // Guest can't save — surface the save-gate modal flow.
        setVehicleChangeModal(null);
        setShowSaveGate(true);
        return;
      }
    }
    // For both "save" (after success) and "discard": reset state, bind new VIN.
    resetForNewVehicle(m.newVin);
    setVehicleChangeModal(null);
  };

  const handleLeave = async (action) => {
    const target = leaveModal?.target;
    if (!target) return;
    if (action === "stay") {
      setLeaveModal(null);
      return;
    }
    if (action === "save") {
      const ok = await persistToServer();
      if (!ok && !isLoggedIn) {
        setLeaveModal(null);
        setShowSaveGate(true);
        return;
      }
    }
    // Mark saved or discard — either way, we let the customer leave.
    // We sign the state so the dirty guard releases, then perform the nav.
    byod.markSaved();
    setLeaveModal(null);
    // Defer to ensure the guard effect's cleanup runs before nav.
    setTimeout(() => {
      if (target === -1) {
        // Browser back — drop our sentinel + go back one more.
        window.history.go(-2);
      } else {
        navigate(target);
      }
    }, 30);
  };

  // ----- Save and Come Back Later affordance -----
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const onSaveClick = async () => {
    setSaveMsg("");
    if (!isLoggedIn) {
      // Guests: gate behind account creation
      setShowSaveGate(true);
      return;
    }
    setSaveBusy(true);
    try {
      const ok = await persistToServer();
      setSaveMsg(ok ? "Saved! We'll keep this here for you." : "Couldn't save right now — please try again.");
    } catch (e) {
      setSaveMsg(formatApiError(e));
    } finally {
      setSaveBusy(false);
      setTimeout(() => setSaveMsg(""), 4000);
    }
  };

  // ----- Step rendering -----
  const stepKey = STEPS[byod.stepIdx]?.key;
  const firstName = byod.state.identity?.first_name || byod.state.contact?.first_name || "";
  const detailHref = vehicle ? `/${vehicle.status}-vehicles/${vehicle.slug}` : urlParam;

  // Mark completed steps for the progress bar (very loose heuristic — any step
  // with material data populated counts as complete).
  const completedKeys = useMemo(() => {
    const set = new Set();
    const s = byod.state;
    if (s.identity?.mode) set.add("start");
    if (vehicle) set.add("vehicle");
    if (s.trade?.has_trade) set.add("trade");
    if (s.money_down?.choice) set.add("money_down");
    if ((s.rebates?.selected?.length || 0) > 0 || (s.rebates?.not_sure?.length || 0) > 0 || (s.rebates?.skipped?.length || 0) > 0) set.add("rebates");
    if (s.rate?.credit_tier || s.rate?.special_apr_id) set.add("rate");
    if ((s.accessories || []).length > 0 || s.accessory_site_interest) set.add("accessories");
    if (byod.requiredProtectionComplete) set.add("required_protection");
    if (Object.values(s.optional_protection?.states || {}).some((v) => v?.status)) set.add("optional_protection");
    if (s.sales_tax?.county) set.add("sales_tax");
    return set;
  }, [byod.state, vehicle, byod.requiredProtectionComplete]);

  return (
    <main id="main-content" className="bg-[var(--ssc-paper)] min-h-screen">
      <Seo
        title="Build My Own Deal | Scott Sells Chevys"
        description="Build your Chevrolet deal one step at a time with Scott. Vehicle, trade, money down, rebates, accessories, and coverages — Scott reviews everything personally."
        canonicalPath="/build-my-own-deal"
        jsonLd={[
          breadcrumbs([{ name: "Home", path: "/" }, { name: "Build My Own Deal", path: "/build-my-own-deal" }]),
        ]}
      />

      <ByodProgressBar
        steps={STEPS}
        currentIdx={byod.stepIdx}
        onStepClick={byod.goTo}
        completedKeys={completedKeys}
      />

      <header className="border-b border-[var(--ssc-line)] bg-white">
        <div className="ssc-container py-4 sm:py-5 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[var(--ssc-blue)]">
              Step {byod.stepIdx + 1} of {STEPS.length}
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-[var(--ssc-navy)] mt-0.5">
              Build My Own Deal
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {saveMsg && (
              <span className="text-xs text-[var(--ssc-ink-2)]" data-testid="byod-save-msg">{saveMsg}</span>
            )}
            <button
              type="button"
              onClick={onSaveClick}
              disabled={saveBusy}
              className="ssc-btn ssc-btn-outline !py-1.5 !text-xs"
              data-testid="byod-save-later"
            >
              {saveBusy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saveBusy ? "Saving..." : "Save and come back later"}
            </button>
          </div>
        </div>
      </header>

      <section className="ssc-container py-6 sm:py-10">
        <div ref={dealTopRef} aria-hidden="true" style={{ scrollMarginTop: "8rem" }} data-testid="byod-deal-top" />
        <div className="max-w-4xl mx-auto">
          {stepKey === "start" && (
            <StartStep byod={byod} onContinue={byod.next} />
          )}
          {stepKey === "vehicle" && (
            <VehicleStep
              byod={byod}
              vehicle={vehicle}
              vehicleStatus={vehicleStatus}
              detailHref={detailHref}
              onContinue={byod.next}
              onBack={byod.back}
              firstName={firstName}
            />
          )}
          {stepKey === "trade" && (
            <TradeStep byod={byod} onContinue={byod.next} onBack={byod.back} firstName={firstName} />
          )}
          {stepKey === "money_down" && (
            <MoneyDownStep byod={byod} onContinue={byod.next} onBack={byod.back} firstName={firstName} />
          )}
          {stepKey === "rebates" && (
            <RebatesStep byod={byod} vin={vehicle?.vin || vinParam} vehicle={vehicle} onContinue={byod.next} onBack={byod.back} firstName={firstName} />
          )}
          {stepKey === "rate" && (
            <RateStep byod={byod} vin={vehicle?.vin || vinParam} vehicle={vehicle} onContinue={byod.next} onBack={byod.back} firstName={firstName} />
          )}
          {stepKey === "accessories" && (
            <AccessoriesStep byod={byod} onContinue={byod.next} onBack={byod.back} firstName={firstName} />
          )}
          {stepKey === "required_protection" && (
            <RequiredProtectionStep byod={byod} onContinue={byod.next} onBack={byod.back} firstName={firstName} />
          )}
          {stepKey === "optional_protection" && (
            <OptionalProtectionStep byod={byod} onContinue={byod.next} onBack={byod.back} firstName={firstName} />
          )}
          {stepKey === "sales_tax" && (
            <SalesTaxStep byod={byod} vehicle={vehicle} onContinue={byod.next} onBack={byod.back} firstName={firstName} />
          )}
          {stepKey === "review" && (
            <ReviewStep
              byod={byod}
              vehicle={vehicle}
              onContinue={byod.next}
              onBack={byod.back}
              onEdit={(key) => byod.goTo(key)}
              firstName={firstName}
            />
          )}
          {stepKey === "submit" && (
            <SubmitStep byod={byod} vehicle={vehicle} dealMath={dealMath} onBack={byod.back} firstName={firstName} />
          )}
        </div>
      </section>

      {/* Save-gate modal for guests trying to save */}
      {showSaveGate && (
        <SaveGate
          byod={byod}
          onClose={() => setShowSaveGate(false)}
          onSaved={async () => {
            setShowSaveGate(false);
            await persistToServer();
            setSaveMsg("Saved! Your deal is on your account now.");
            setTimeout(() => setSaveMsg(""), 4000);
          }}
        />
      )}

      {/* Vehicle change confirmation modal */}
      {vehicleChangeModal && (
        <ConfirmModal
          dataTestid="byod-vehicle-change-modal"
          title="Save this deal before changing vehicles?"
          body="You have unsaved work on this vehicle. Would you like to save it before starting a new deal?"
          actions={[
            { label: "Save Deal & Change Vehicle", variant: "primary", onClick: () => handleVehicleChange("save"), testid: "byod-vehicle-change-save" },
            { label: "Discard Changes & Change Vehicle", variant: "outline", onClick: () => handleVehicleChange("discard"), testid: "byod-vehicle-change-discard" },
            { label: "Keep Working", variant: "ghost", onClick: () => handleVehicleChange("keep"), testid: "byod-vehicle-change-keep" },
          ]}
        />
      )}

      {/* Leave-page confirmation modal */}
      {leaveModal && (
        <ConfirmModal
          dataTestid="byod-leave-modal"
          title="Save this deal before leaving?"
          body="Your current deal has unsaved changes."
          actions={[
            { label: "Save & Leave", variant: "primary", onClick: () => handleLeave("save"), testid: "byod-leave-save" },
            { label: "Leave Without Saving", variant: "outline", onClick: () => handleLeave("leave"), testid: "byod-leave-discard" },
            { label: "Stay Here", variant: "ghost", onClick: () => handleLeave("stay"), testid: "byod-leave-stay" },
          ]}
        />
      )}
    </main>
  );
}

function ConfirmModal({ title, body, actions, dataTestid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-3" data-byod-modal data-testid={dataTestid}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 sm:p-6">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={20} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-extrabold text-[var(--ssc-navy)] leading-tight">{title}</h3>
            <p className="text-sm text-[var(--ssc-ink-2)] mt-1.5 leading-relaxed">{body}</p>
          </div>
        </div>
        <div className="mt-5 flex flex-col gap-2">
          {actions.map((a, i) => (
            <button
              key={i}
              type="button"
              onClick={a.onClick}
              className={`ssc-btn ${a.variant === "primary" ? "ssc-btn-primary" : a.variant === "outline" ? "ssc-btn-outline" : "ssc-btn-outline !border-transparent !text-[var(--ssc-ink-2)] hover:!bg-[var(--ssc-paper)]"}`}
              data-testid={a.testid}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SaveGate({ byod, onClose, onSaved }) {
  const { signup } = useAuth();
  const id = byod.state.identity || {};
  const [firstName, setFN] = useState(id.first_name || "");
  const [lastName, setLN] = useState(id.last_name || "");
  const [phone, setPhone] = useState(id.phone || "");
  const [email, setEmail] = useState(id.email || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const create = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!firstName.trim() || !lastName.trim() || !phone.trim() || !email.trim() || !password) {
      return setErr("Please fill in every field — all are required to save your deal.");
    }
    if (password.length < 8) return setErr("Password must be at least 8 characters.");
    setBusy(true);
    try {
      await signup({
        name: `${firstName.trim()} ${lastName.trim()}`,
        email: email.trim(),
        phone: phone.trim(),
        password,
      });
      byod.update("identity", {
        mode: "account",
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        email: email.trim(),
      });
      byod.update("contact", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        email: email.trim(),
      });
      await onSaved();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 grid place-items-center p-3" data-testid="byod-save-gate">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 sm:p-6">
        <h3 className="text-lg font-extrabold text-[var(--ssc-navy)]">Save your deal and come back later</h3>
        <p className="text-sm text-[var(--ssc-ink-2)] mt-1.5">
          To save your deal and come back later, please create an account so we know
          where to keep your deal.
        </p>
        <form onSubmit={create} className="grid grid-cols-2 gap-2.5 mt-4">
          <label className="block">
            <span className="ssc-label">First name *</span>
            <input className="ssc-input" value={firstName} onChange={(e) => setFN(e.target.value)} autoComplete="given-name" data-testid="byod-save-gate-firstname" />
          </label>
          <label className="block">
            <span className="ssc-label">Last name *</span>
            <input className="ssc-input" value={lastName} onChange={(e) => setLN(e.target.value)} autoComplete="family-name" data-testid="byod-save-gate-lastname" />
          </label>
          <label className="block">
            <span className="ssc-label">Phone *</span>
            <input className="ssc-input" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" data-testid="byod-save-gate-phone" />
          </label>
          <label className="block">
            <span className="ssc-label">Email *</span>
            <input className="ssc-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="byod-save-gate-email" />
          </label>
          <label className="block col-span-2">
            <span className="ssc-label">Password * <span className="text-xs font-normal text-[var(--ssc-ink-2)]">(8+ characters)</span></span>
            <input className="ssc-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" data-testid="byod-save-gate-password" />
          </label>
          {err && <div className="col-span-2 text-sm text-[var(--ssc-red)]" data-testid="byod-save-gate-error">{err}</div>}
          <div className="col-span-2 flex flex-wrap gap-2 justify-end mt-2">
            <button type="button" onClick={onClose} className="ssc-btn ssc-btn-outline" data-testid="byod-save-gate-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary" data-testid="byod-save-gate-submit">
              {busy ? "Saving..." : "Create & Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
